// FilterControls.js
import React, { useMemo } from 'react';
import {
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
} from '@mui/material';
import { getBusinessUnit } from './helpers';

export const FilterControls = ({
  showFilters,
  filters,
  handleFilterChange,
  locations = [], // Provide default empty array
  isMobile,
  selectedSiteTypes = [], // Provide default empty array
}) => {
  // Calculate available options based on current filters
  const dynamicOptions = useMemo(() => {
    // Guard against null/undefined locations
    if (!Array.isArray(locations)) {
      return {
        continents: [],
        countries: [],
        businessNames: []
      };
    }

    // Start with all locations
    let filteredLocs = locations;

    // Apply business unit filter if selected
    if (filters?.businessUnit) {
      filteredLocs = filteredLocs.filter(
        loc => getBusinessUnit(loc.abbreviation) === filters.businessUnit
      );
    }

    // Apply continent filter if selected
    if (filters?.continent) {
      filteredLocs = filteredLocs.filter(
        loc => loc.continent === filters.continent
      );
    }

    // Apply country filter if selected
    if (filters?.country) {
      filteredLocs = filteredLocs.filter(
        loc => loc.country === filters.country
      );
    }

    // Apply business name filter if selected
    if (filters?.businessName) {
      filteredLocs = filteredLocs.filter(
        loc => loc.businessName === filters.businessName
      );
    }

    // Apply site type filter - guard against undefined selectedSiteTypes
    if (Array.isArray(selectedSiteTypes) && selectedSiteTypes.length > 0) {
      filteredLocs = filteredLocs.filter(
        loc => loc.size && selectedSiteTypes.includes(loc.size)
      );
    }

    // Extract unique values for each filter type with null checks
    return {
      // Get continents based on business unit filter only
      continents: [...new Set(
        locations
          .filter(loc => !filters?.businessUnit || getBusinessUnit(loc.abbreviation) === filters.businessUnit)
          .map(loc => loc.continent)
          .filter(Boolean) // Remove null/undefined values
      )].sort(),

      // Get countries based on business unit and continent filters
      countries: [...new Set(
        locations
          .filter(loc => {
            const matchesBusinessUnit = !filters?.businessUnit || getBusinessUnit(loc.abbreviation) === filters.businessUnit;
            const matchesContinent = !filters?.continent || loc.continent === filters.continent;
            return matchesBusinessUnit && matchesContinent;
          })
          .map(loc => loc.country)
          .filter(Boolean) // Remove null/undefined values
      )].sort(),

      // Get business names based on all other filters
      businessNames: [...new Set(
        filteredLocs
          .map(loc => loc.businessName)
          .filter(Boolean) // Remove null/undefined values
      )].sort(),
    };
  }, [locations, filters, selectedSiteTypes]);

  // Early return if filters should not be shown
  if (!showFilters) return null;

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: 2,
        }}
      >
        {/* Business Unit Filter */}
        <FormControl fullWidth>
          <InputLabel>Business Unit</InputLabel>
          <Select
            value={filters?.businessUnit || ''}
            label="Business Unit"
            onChange={(e) => handleFilterChange('businessUnit', e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="MKS">MKS</MenuItem>
            <MenuItem value="MSD">MSD</MenuItem>
          </Select>
        </FormControl>

        {/* Continent Filter */}
        <Autocomplete
          value={filters?.continent || null}
          onChange={(event, newValue) => handleFilterChange('continent', newValue)}
          options={dynamicOptions.continents}
          renderInput={(params) => (
            <TextField {...params} label="Continent" />
          )}
          fullWidth
          onInputChange={(event, value, reason) => {
            if (reason === 'clear') {
              handleFilterChange('country', null);
              handleFilterChange('businessName', null);
            }
          }}
        />

        {/* Country Filter */}
        <Autocomplete
          value={filters?.country || null}
          onChange={(event, newValue) => handleFilterChange('country', newValue)}
          options={dynamicOptions.countries}
          renderInput={(params) => <TextField {...params} label="Country" />}
          fullWidth
          onInputChange={(event, value, reason) => {
            if (reason === 'clear') {
              handleFilterChange('businessName', null);
            }
          }}
          disabled={!filters?.continent}
        />

        {/* Business Name Filter */}
        <Autocomplete
          value={filters?.businessName || null}
          onChange={(event, newValue) => handleFilterChange('businessName', newValue)}
          options={dynamicOptions.businessNames}
          renderInput={(params) => (
            <TextField {...params} label="Business Name" />
          )}
          fullWidth
          disabled={!filters?.country}
        />
      </Box>
    </Paper>
  );
};